import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    useMediaQuery,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Paper,
    Skeleton,
    Stack,
    Snackbar,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createStudentServiceRequest, getCompletedExams, getReEvaluation } from "../../../utils/apiService";
import KenTable from "../../../components/KenTable";

const rowsTransactionHistory = [
    {
        id: 1,
        date: "13-08-2025",
        feeType: "Re-evaluation",
        fee: "INR 12,000",
        mode: "N/A",
        status: "Paid",
        receipt: true,
    },
    {
        id: 2,
        date: "14-08-2025",
        feeType: "Re-evaluation",
        fee: "INR 12,000",
        mode: "N/A",
        status: "Paid",
        receipt: true,
    },
    {
        id: 3,
        date: "15-08-2025",
        feeType: "Re-examination",
        fee: "INR 12,000",
        mode: "N/A",
        status: "Initiated, not completed",
        receipt: false,
    },
    {
        id: 4,
        date: "16-08-2025",
        feeType: "Examination",
        fee: "INR 11,500",
        mode: "N/A",
        status: "Paid",
        receipt: true,
    },
    {
        id: 5,
        date: "17-08-2025",
        feeType: "Re-examination",
        fee: "INR 12,000",
        mode: "N/A",
        status: "Initiated, not completed",
        receipt: false,
    },
];

type EligibleCourse = {
    id: string;
    courseConnectionId?: string | null;
    course: string;
    semester: string;
    courseCode: string;
    credits: number;
    format: string;
    type: string;
    dueDate: string;
    examTime: string;
    fee: number;
    requestType?: string;
    examRequest?: boolean;
    backendId?: string;
    examDateRaw?: string | null;
    examTimeRaw?: string | null;
};

const mockEligibleCourses: EligibleCourse[] = [
    {
        id: "mock-1",
        backendId: "mock-1",
        courseConnectionId: "mock-cc-1",
        course: "Network Analysis & Synthesis",
        semester: "I",
        courseCode: "MBAG MGT 103",
        credits: 3,
        format: "Theory",
        type: "Regular",
        dueDate: "15-03-2024",
        examTime: "--",
        fee: 500,
    },
    {
        id: "mock-2",
        backendId: "mock-2",
        courseConnectionId: "mock-cc-2",
        course: "Signals & Systems",
        semester: "II",
        courseCode: "MBAG MGT 100",
        credits: 4,
        format: "Theory",
        type: "Backlog",
        dueDate: "15-03-2024",
        examTime: "--",
        fee: 600,
    },
    {
        id: "mock-3",
        backendId: "mock-3",
        courseConnectionId: "mock-cc-3",
        course: "Human Resource Management",
        semester: "II",
        courseCode: "MBAG MGT 001",
        credits: 3,
        format: "Theory",
        type: "Regular",
        dueDate: "15-03-2024",
        examTime: "--",
        fee: 550,
    },
    {
        id: "mock-4",
        backendId: "mock-4",
        courseConnectionId: "mock-cc-4",
        course: "VLSI Design",
        semester: "I",
        courseCode: "MBAG MGT 104",
        credits: 4,
        format: "Practical",
        type: "Regular",
        dueDate: "15-03-2024",
        examTime: "--",
        fee: 700,
    },
    {
        id: "mock-5",
        backendId: "mock-5",
        courseConnectionId: "mock-cc-5",
        course: "Microwave Engineering",
        semester: "II",
        courseCode: "MBAG MGT 103",
        credits: 5,
        format: "Theory",
        type: "Regular",
        dueDate: "15-03-2024",
        examTime: "--",
        fee: 650,
    },
];

const requestStatusColors: Record<string, { bg: string; color: string }> = {
    Approved: { bg: "#C1DCC3", color: "#1F4E21" },
    Requested: { bg: "#FFF2CC", color: "#8A6D1A" },
    Rejected: { bg: "#F1BFBF", color: "#591414" },
    Eligible: { bg: "#C1DCC3", color: "#1F4E21" },
};

const transactionStatusColors: Record<string, { bg: string; color: string }> = {
    Paid: { bg: "#C1DCC3", color: "#1F4E21" },
    "Initiated, not completed": { bg: "#FFF2CC", color: "#8A6D1A" },
};

type RequestRow = {
    id: number;
    courseName: string;
    courseCode: string;
    semester: string;
    credits: number;
    format: string;
    type: string;
    requestedOn: string;
    requestedOnRaw: string | null;
    status: string;
    eligibility: string;
    requestType: string;
};

type TransactionRow = (typeof rowsTransactionHistory)[number];

const formatDateForDisplay = (rawDate?: string | null) => {
    if (!rawDate) return "Date unavailable";
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) {
        return rawDate;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const toArray = (payload: unknown): any[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === "object") {
        const container = payload as Record<string, unknown>;
        const possibleArrayKeys = ["data", "records", "result", "items"];
        for (const key of possibleArrayKeys) {
            const value = container[key];
            if (Array.isArray(value)) {
                return value;
            }
        }
        return [payload];
    }
    return [];
};

const normalizeRequestTypes = (value?: string | null) => {
    if (!value) return [];
    return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

const mapRequestData = (payload: unknown): RequestRow[] => {
    return toArray(payload).map((item, index) => {
        const entry = item as Record<string, any>;
        const creditsValue = Number(entry.credits);

        return {
            id: index + 1,
            courseName: entry.courseName ?? "--",
            courseCode: entry.courseCode ?? "--",
            semester: entry.semester ? `Semester ${entry.semester}` : "N/A",
            credits: Number.isFinite(creditsValue) ? creditsValue : 0,
            format: entry.examFormat ?? entry.format ?? "--",
            type: entry.examType ?? entry.type ?? "--",
            requestedOn: formatDateForDisplay(entry.startDate),
            requestedOnRaw: entry.startDate ?? null,
            status: entry.status ?? entry.requestStatus ?? entry.eligibilityStatus ?? "Requested",
            eligibility: entry.eligibilityStatus ?? "--",
            requestType: entry.requestType ?? "--",
        };
    });
};

const columnsTransactionHistory = [
    { field: "date", headerName: "Date" },
    { field: "feeType", headerName: "Fee type" },
    { field: "fee", headerName: "Fee" },
    { field: "mode", headerName: "Mode" },
    {
        field: "status",
        headerName: "Status",
        renderCell: ({ value }: any) => {
            return (
                <Chip
                    label={value}
                    sx={{
                        borderRadius: "16px",
                        backgroundColor: transactionStatusColors[value]?.bg || "#eee",
                        color: transactionStatusColors[value]?.color || "#333",
                        fontWeight: 500,
                        px: 1.5,
                    }}
                />
            );
        },
    },
    {
        field: "receipt",
        headerName: "Receipt",
        renderCell: ({ value }: any) =>
            value ? (
                <Box component="span" sx={{ cursor: "pointer", fontWeight: 600 }}>
                    Link
                </Box>
            ) : (
                "--"
            ),
    },
];

const columns = [
    { field: "courseName", headerName: "Course name" },
    { field: "courseCode", headerName: "Course code" },
    { field: "semester", headerName: "Semester" },
    { field: "credits", headerName: "Credits" },
    { field: "format", headerName: "Format" },
    { field: "type", headerName: "Type" },
    { field: "requestedOn", headerName: "Requested on" },
    {
        field: "status",
        headerName: "Status",
        renderCell: ({ value }: any) => {
            return (
                <Chip
                    label={value}
                    sx={{
                        borderRadius: "16px",
                        backgroundColor: requestStatusColors[value]?.bg || "#eee",
                        color: requestStatusColors[value]?.color || "#333",
                        fontWeight: 500,
                        px: 1.5,
                    }}
                />
            );
        },
    },
];

const Requests = () => {
    const [activeView, setActiveView] = useState<"Re-evaluation" | "reexamination" | "transactionHistory">("Re-evaluation");
    const [open, setOpen] = useState(false);
    const [modalRequestType, setModalRequestType] = useState<"Re-evaluation" | "Re-examination">("Re-evaluation");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [eligibleCourses, setEligibleCourses] = useState<EligibleCourse[]>(mockEligibleCourses);
    const [isLoadingEligibleCourses, setIsLoadingEligibleCourses] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({ open: false, message: "", severity: "success" });
    const [fileUploadBase64, setFileUploadBase64] = useState("");
    const [fileInputKey, setFileInputKey] = useState(0);
    const isMobile = useMediaQuery("(max-width:600px)");
    const [requestRows, setRequestRows] = useState<RequestRow[]>([]);
    const [isLoadingRequests, setIsLoadingRequests] = useState(true);

    const handleOpen = () => {
        setModalRequestType(activeView === "reexamination" ? "Re-examination" : "Re-evaluation");
        setOpen(true);
    };
    const handleClose = () => {
        setSelectedIds([]);
        setFileUploadBase64("");
        setFileInputKey((prev) => prev + 1);
        setOpen(false);
    };

    const [open1, setOpen1] = useState(false);
    const handleOpen1 = () => setOpen1(true);
    const handleClose1 = () => {
        setFileUploadBase64("");
        setFileInputKey((prev) => prev + 1);
        setOpen1(false);
    };
    const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));
    const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            setFileUploadBase64("");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === "string") {
                const base64 = result.includes(",") ? result.split(",")[1] : result;
                setFileUploadBase64(base64);
            } else {
                setFileUploadBase64("");
            }
        };
        reader.readAsDataURL(file);
    };
    const handleSubmit1 = async () => {
        if (!selectedCourses.length) return;

        const contactId = localStorage.getItem("contactId");
        const courseConnections = Array.from(
            new Set(
                selectedCourses
                    .map((course) => course.courseConnectionId ?? course.backendId ?? course.id)
                    .filter((value): value is string => Boolean(value))
            )
        );

        const examId = Array.from(
            new Set(
                selectedCourses
                    .map((course: any) => course.examRecordId ?? course.backendId ?? course.id)
                    .filter((value): value is string => Boolean(value))
            )
        )

        if (!contactId || !courseConnections.length) {
            console.error("Unable to submit exam request. Missing contactId or courses.");
            setSnackbar({
                open: true,
                message: !contactId ? "Contact ID missing. Please re-login and try again." : "Select at least one exam to submit.",
                severity: "error",
            });
            return;
        }

        const trimmedUploadBase64 = fileUploadBase64.trim();
        const payload = {
            contactId,
            courseConnections,
            submitForApproval: true,
            recordTypeName: modalRequestType,
            examId,
            ...(trimmedUploadBase64 ? { fileUpload: trimmedUploadBase64 } : {}),
        };

        try {
            setIsSubmitting(true);
            await createStudentServiceRequest(payload);
            console.log("Submitting selected exams:", payload);
            setOpen1(false);
            handleClose();
            setSnackbar({
                open: true,
                message: "Enrollment request submitted successfully.",
                severity: "success",
            });
        } catch (error) {
            console.error("Failed to submit exam request", error);
            setSnackbar({
                open: true,
                message: "Failed to submit request. Please try again.",
                severity: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelect = (id: string) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };

    const eligibleCoursesForModal = React.useMemo(() => {
        return eligibleCourses.filter((row) => {
            if (row.examRequest) return false;
            const requestTypes = normalizeRequestTypes(row.requestType);
            if (!requestTypes.length) return true;
            return requestTypes.includes(modalRequestType);
        });
    }, [eligibleCourses, modalRequestType]);

    const selectedCourses = eligibleCoursesForModal.filter((row) => selectedIds.includes(row.id));
    const totalFee = selectedCourses.reduce((sum, row) => sum + row.fee, 0);

    const toDateValue = (row: RequestRow) => {
        if (row.requestedOnRaw) {
            const parsed = new Date(row.requestedOnRaw).getTime();
            if (!Number.isNaN(parsed)) return parsed;
        }
        const parts = row.requestedOn.split("-");
        if (parts.length === 3) {
            const [day, month, year] = parts.map((value) => Number(value));
            if (Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)) {
                return new Date(year, month - 1, day).getTime();
            }
        }
        return 0;
    };

    const sortByRequestedOn = (rows: RequestRow[]) =>
        [...rows].sort((a, b) => toDateValue(b) - toDateValue(a));

    const getRows = () => {
        const filteredRevaluation = requestRows.filter((row) => row.requestType === "Re-evaluation");
        const filteredReexamination = requestRows.filter((row) => row.requestType === "Re-examination");

        switch (activeView) {
            case "Re-evaluation":
                return sortByRequestedOn(filteredRevaluation);
            case "reexamination":
                return sortByRequestedOn(filteredReexamination);
            case "transactionHistory":
                return rowsTransactionHistory;
            default:
                return [];
        }
    };

    const currentRows = getRows();
    const currentColumns = activeView === "transactionHistory" ? columnsTransactionHistory : columns;
    const isLoadingCurrentView = activeView !== "transactionHistory" && isLoadingRequests;

    const renderEmptyState = (title: string, description: string) => (
        <Box
            sx={{
                mt: 4,
                px: 3,
                py: 6,
                textAlign: "center",
                borderRadius: "16px",
                backgroundColor: "#F8FAFC",
                border: "1px dashed #CBD5E1",
            }}
        >
            <Typography fontWeight={600} color="#1F2937">
                {title}
            </Typography>
            <Typography fontSize={13} color="#64748B" mt={0.5}>
                {description}
            </Typography>
        </Box>
    );

    const renderSkeletonRows = (count = 4) => (
        <Stack spacing={2} mt={2}>
            {Array.from({ length: count }).map((_, idx) => (
                <Paper key={idx} sx={{ p: 2 }}>
                    <Skeleton variant="text" width="30%" />
                    <Skeleton variant="text" width="50%" />
                    <Skeleton variant="rectangular" height={32} sx={{ mt: 1, borderRadius: 1 }} />
                </Paper>
            ))}
        </Stack>
    );

    const renderRequestsMobile = (data: RequestRow[], isLoading: boolean) => {
        if (isLoading) {
            return (
                <Stack spacing={2} mt={2}>
                    {Array.from({ length: 4 }).map((_, idx) => (
                        <Paper key={idx} sx={{ p: 2 }}>
                            <Skeleton variant="text" width="60%" />
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="rectangular" height={28} sx={{ mt: 1, borderRadius: 1 }} />
                        </Paper>
                    ))}
                </Stack>
            );
        }

        if (!data.length) {
            return renderEmptyState("No requests yet", "Submit a request to view it here once it has been created.");
        }

        const grouped: any = new Map<string, RequestRow[]>();
        sortByRequestedOn(data).forEach((row) => {
            const date = row.requestedOn || "Date unavailable";
            if (!grouped.has(date)) {
                grouped.set(date, []);
            }
            grouped.get(date)!.push(row);
        });

        return (
            <Box mt={3}>
                <Typography fontWeight={600} fontSize={14} color="#1F2937">
                    All requests
                </Typography>
                {[...grouped.entries()].map(([date, items]) => (
                    <Box key={date} mt={2}>
                        <Typography fontSize={13} fontWeight={500} color="#64748B">
                            {date}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
                            {items.map((row: any) => (
                                <Box
                                    key={row.id}
                                    sx={{
                                        p: 2,
                                        backgroundColor: "#FFFFFF",
                                        borderRadius: "16px",
                                        boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.08)",
                                        border: "1px solid #E2E8F0",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.5,
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography fontWeight={600} fontSize={14} color="#1F2937">
                                            {row.courseName}
                                        </Typography>
                                        <Chip
                                            label={row.status}
                                            sx={{
                                                borderRadius: "12px",
                                                backgroundColor: requestStatusColors[row.status]?.bg || "#E2E8F0",
                                                color: requestStatusColors[row.status]?.color || "#1E293B",
                                                fontWeight: 500,
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Course code
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.courseCode}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Semester
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.semester}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Credits
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.credits}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Format
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.format}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Type
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.type}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderTransactionsMobile = (data: TransactionRow[]) => {
        if (!data.length) {
            return renderEmptyState(
                "No transactions yet",
                "Transactions will appear here after you complete a payment."
            );
        }

        return (
            <Box mt={3} display="flex" flexDirection="column" gap={2}>
                {data.map((row) => (
                    <Box
                        key={row.id}
                        sx={{
                            p: 2,
                            backgroundColor: "#FFFFFF",
                            borderRadius: "16px",
                            boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.08)",
                            border: "1px solid #E2E8F0",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600} fontSize={14} color="#1F2937">
                                {row.feeType}
                            </Typography>
                            <Chip
                                label={row.status}
                                sx={{
                                    borderRadius: "12px",
                                    backgroundColor: transactionStatusColors[row.status]?.bg || "#E2E8F0",
                                    color: transactionStatusColors[row.status]?.color || "#1E293B",
                                    fontWeight: 500,
                                    height: 24,
                                }}
                            />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography fontSize={13} color="text.secondary">
                                Date
                            </Typography>
                            <Typography fontSize={13} fontWeight={500}>
                                {row.date}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography fontSize={13} color="text.secondary">
                                Fee
                            </Typography>
                            <Typography fontSize={13} fontWeight={500}>
                                {row.fee}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography fontSize={13} color="text.secondary">
                                Mode
                            </Typography>
                            <Typography fontSize={13} fontWeight={500}>
                                {row.mode}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography fontSize={13} color="text.secondary">
                                Receipt
                            </Typography>
                            <Typography fontSize={13} fontWeight={500}>
                                {row.receipt ? "Download receipt" : "No receipt"}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderMobileContent = () =>
        activeView === "transactionHistory"
            ? renderTransactionsMobile(currentRows as TransactionRow[])
            : renderRequestsMobile(currentRows as RequestRow[], isLoadingCurrentView);

    useEffect(() => {
        let isMounted = true;

        const fetchEligibleCourses = async () => {
            setIsLoadingEligibleCourses(true);
            try {
                const response = await getCompletedExams();
                const payload = response?.data ?? response;
                const exams = toArray(payload?.eligibleRequestExams);

                const formatIndianTime = (timeString?: string) => {
                    if (!timeString) return "--";
                    const [timePart = "00:00:00"] = timeString.split(".");
                    const [hourStr = "00", minuteStr = "00"] = timePart.split(":");
                    let hours = parseInt(hourStr, 10);
                    if (Number.isNaN(hours)) return "--";
                    const minutes = minuteStr.padStart(2, "0");
                    const period = hours >= 12 ? "PM" : "AM";
                    hours = hours % 12 || 12;
                    return `${hours.toString().padStart(2, "0")}:${minutes} ${period}`;
                };

                const mappedEligibleCourses = exams.map((item: any, index: number) => {
                    const creditsValue = Number(item.credits);
                    const feeValue = Number(item.fee ?? item.amount ?? item.totalFee);
                    const backendId = item.courseConnectionId ?? item.examScheduleId ?? item.id;
                    const semesterLabel = item.semester ? `Semester ${item.semester}` : "N/A";
                    const formattedDate = formatDateForDisplay(item.startDate);

                    return {
                        id: String(backendId ?? index + 1),
                        backendId: backendId,
                        courseConnectionId: item.courseConnectionId ?? null,
                        course: item.courseName ?? "--",
                        courseCode: item.courseCode ?? "--",
                        semester: semesterLabel,
                        credits: Number.isFinite(creditsValue) ? creditsValue : 0,
                        // courseConnectionId : item?.courseConnectionId,
                        examRecordId: item?.examRecordId,
                        format: item.examFormat ?? item.format ?? "--",
                        type: item.examType ?? item.requestType ?? "--",
                        requestType: item.requestType ?? item.status,
                        examRequest: Boolean(item.examRequest),
                        dueDate: formattedDate,
                        examTime: formatIndianTime(item.startTime),
                        fee: Number.isFinite(feeValue) ? feeValue : 0,
                        examDateRaw: item.startDate ?? null,
                        examTimeRaw: item.startTime ?? null,
                    } as EligibleCourse;
                });

                if (isMounted) {
                    setEligibleCourses(mappedEligibleCourses);
                }
            } catch (error) {
                console.error("Error in fetching completed exams:", error);
                if (isMounted) {
                    setEligibleCourses([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEligibleCourses(false);
                }
            }
        };

        fetchEligibleCourses();

        const fetchData = async () => {
            try {
                if (isMounted) {
                    setIsLoadingRequests(true);
                }

                const response = await getReEvaluation();
                const mappedRows = mapRequestData(response?.data?.RequestExams ?? response);

                if (isMounted) {
                    setRequestRows(mappedRows);
                }
            } catch (error) {
                console.error("Failed to load re-evaluation requests", error);
                if (isMounted) {
                    setRequestRows([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRequests(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Box sx={{ position: "relative", pb: isMobile ? "84px" : 0 }}>
            <Box mb={3} display="flex" flexDirection="column" gap={isMobile ? 2 : 3}>
                {/* <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                    gap={isMobile ? 2 : 0}
                >
                    <Box>
                        <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>Requests overview</Typography>
                        
                    </Box>
                    {!isMobile && (
                        <Button variant="secondary" onClick={handleOpen} sx={{ minWidth: 160, color: "white !important" }}>
                            Apply now
                        </Button>
                    )}
                </Box> */}
                <Box display="flex" justifyContent="space-between" alignItems={'center'} >
                    <Box
                        display="flex"
                        flexDirection={"row"}
                        justifyContent="space-between"
                        sx={{
                            display: "flex",
                            gap: 1,
                            backgroundColor: "#F6F8FB",
                            borderRadius: "12px",
                            padding: "4px",
                            width: isMobile ? "100%" : "fit-content",
                        }}
                    >
                        <>

                            {["Re-evaluation", "reexamination",].map((tab) => (
                                <Button

                                    key={tab}
                                    onClick={() => setActiveView(tab as typeof activeView)}
                                    sx={{
                                        flex: isMobile ? 1 : "initial",
                                        backgroundColor: activeView === tab ? "#1a1a2e" : "transparent",
                                        color: activeView === tab ? "#FFFFFF" : "#4B5563",
                                        borderRadius: "10px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: isMobile ? 14 : 14,
                                        py: isMobile ? 1 : 1.25,
                                        px: isMobile ? 0.6 : 2,
                                        "&:hover": {
                                            backgroundColor: activeView === tab ? "#111827" : "#E2E8F0",
                                        },
                                    }}
                                >
                                    {tab === "Re-evaluation" ? "Re-evaluation" : tab === "reexamination" && "Re-examination"}
                                </Button>
                            ))}
                        </>

                    </Box>
                    <Box>
                        {!isMobile && (
                            <Button size="medium" variant="secondary" onClick={handleOpen} sx={{ color: "white !important" }}>
                                Apply now
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {isMobile ? (
                renderMobileContent()
            ) : isLoadingCurrentView ? (
                renderSkeletonRows()
            ) : currentRows.length ? (
                <KenTable columns={currentColumns} rows={currentRows} />
            ) : (
                renderEmptyState(
                    activeView === "transactionHistory" ? "No transactions yet" : "No requests yet",
                    activeView === "transactionHistory"
                        ? "Transactions will appear here after you complete a payment."
                        : "Submit a request to view it here once it has been created."
                )
            )}
            {/* Apply now button for mobile view */}
            {/* {isMobile && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 480,
                            margin: "0 auto",
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: "16px",
                                backgroundColor: "#FFFFFF",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.18)",
                                padding: "12px",
                                marginBottom: "58px",
                            }}
                        >
                            <Button
                                variant="secondary"
                                fullWidth
                                onClick={handleOpen}
                                sx={{
                                    color: "white !important",
                                    borderRadius: "12px",
                                    py: 1.5,
                                    fontWeight: 600,
                                }}
                            >
                                Apply now
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )} */}

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        backgroundColor: "#F6F8FB",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pr: 2,
                    }}
                >
                    <Box>
                        <Typography fontWeight={700} fontSize={18}>
                            {modalRequestType}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: "#475569" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ py: 0 }}>
                    {/* <Box
                        sx={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: "16px",
                            border: "1px solid #E2E8F0",
                            px: 3,
                            py: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography fontWeight={600}>Eligible courses</Typography>
                        
                    </Box> */}

                    <TableContainer component={Paper} sx={{ borderRadius: "16px", overflow: "hidden" }}>
                        {isLoadingEligibleCourses ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : eligibleCoursesForModal.length ? (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Course</TableCell>
                                        <TableCell>Semester</TableCell>
                                        <TableCell>Course code</TableCell>
                                        <TableCell>Credits</TableCell>
                                        <TableCell>Format</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Exam date</TableCell>
                                        <TableCell>Exam time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {eligibleCoursesForModal.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => handleSelect(row.id)}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedIds.includes(row.id)}
                                                    onClick={(event) => event.stopPropagation()}
                                                    onChange={() => handleSelect(row.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{row.course}</TableCell>
                                            <TableCell>{row.semester}</TableCell>
                                            <TableCell>{row.courseCode}</TableCell>
                                            <TableCell>{row.credits}</TableCell>
                                            <TableCell>{row.format}</TableCell>
                                            <TableCell>{row.type}</TableCell>
                                            <TableCell>{row.dueDate}</TableCell>
                                            <TableCell>{row.examTime}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Box py={5} textAlign="center">
                                <Typography fontWeight={600}>No eligible exams available</Typography>
                                <Typography fontSize={13} color="text.secondary">
                                    Once exams are scheduled for you, they will appear here for enrollment.
                                </Typography>
                            </Box>
                        )}
                    </TableContainer>
                </DialogContent>

                <DialogActions
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "stretch" : "center",
                        justifyContent: isMobile ? "flex-start" : "space-between",
                        gap: isMobile ? 1.5 : 0,
                        px: isMobile ? 2 : 3,
                        py: 2,
                        borderTop: "1px solid #e0e0e0",
                    }}
                >
                    {/* <Typography fontWeight={600} fontSize={isMobile ? 16 : 15}>
                        Total fee: INR {totalFee.toLocaleString()}
                    </Typography> */}
                    <Box></Box>
                    <Button
                        variant="secondary"
                        disabled={selectedIds.length === 0}
                        sx={{ color: "white !important", borderRadius: "10px" }}
                        onClick={() => setOpen1(true)}
                        fullWidth={isMobile}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={open1}
                onClose={handleClose1}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        padding: "16px",
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, pb: 1 }}>Confirm Enrollment?</DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                        Once you enroll for these exams, you will not be able to add or switch courses. Please review your
                        selections carefully before submitting.
                    </Typography>
                    {selectedCourses.length > 0 && (
                        <Stack spacing={1.5} mt={2}>
                            {selectedCourses.map((course) => (
                                <Box key={course.id} display="flex" flexDirection="column">
                                    <Typography fontSize={13} fontWeight={600}>
                                        {course.course}
                                    </Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        {course.courseCode} • {course.semester} • {course.examTime}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                    <TextField
                        key={fileInputKey}
                        fullWidth
                        type="file"
                        label="Upload Payment receipt"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: "application/pdf,image/*" }}
                        onChange={handleFileUploadChange}
                        margin="normal"
                        helperText="Upload PDF or image. Sent as base64 with the request."
                    />
                </DialogContent>

                <DialogActions sx={{ justifyContent: "end", px: 2, pb: 2 }}>
                    <Button onClick={handleClose1} sx={{ textTransform: "none", color: "text.secondary" }}>
                        Review selection
                    </Button>
                    <Button variant="secondary" onClick={handleSubmit1} disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Requests;
